import { useEffect, useState } from 'react';
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import api from '@/lib/apiClient';
import { showAlert } from '@/components/ui/GlobalAlert';
import { formatApiError } from '@/lib/utils';
import { showConfirm } from '@/components/ui/GlobalConfirm';
import {
  Plus, Search, Users, Phone, Mail, X, Loader2,
  ChevronDown, ChevronUp, Edit3, Trash2, FileText,
  Building2
} from 'lucide-react';

const IC = "w-full bg-white/[0.04] border border-[rgba(201,164,76,0.15)] text-[#F8F6F0] placeholder-[#888]/50 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#C9A44C] focus:ring-1 focus:ring-[#C9A44C]/40 transition-all";

const FUEROS = ['Civil', 'Penal', 'Laboral', 'Familia', 'Comercial', 'Contencioso Administrativo', 'Constitucional', 'Sucesiones', 'Concursos y Quiebras', 'Previsional', 'Previsional Social', 'Administrativo'];

/* ─── Modal crear cliente ─────────────────────────────────────── */
function ModalCrear({ onClose, onCreated }) {
  const [f, setF] = useState({
    nombre: '', apellido: '', tipo_persona: 'fisica',
    dni_cuit: '', telefono: '', email: '',
    domicilio_real: '', domicilio_legal: '',
    caratula: '', fuero: '', juzgado: '',
  });
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (ev) => {
    ev.preventDefault(); setErr(''); setSaving(true);
    try {
      await api.post('/api/clientes/', f);
      onCreated();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail, 'Error al crear cliente'));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-lg my-4 bg-[#0E0E0E] border border-[rgba(201,164,76,0.2)] rounded-2xl p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>Nuevo cliente</h3>
          <button onClick={onClose} className="text-[#888] hover:text-[#F8F6F0]"><X className="w-5 h-5" /></button>
        </div>
        {err && <div className="bg-red-500/10 border border-red-500/25 rounded-lg p-2.5 mb-4 text-red-400 text-xs">{err}</div>}
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input className={IC} placeholder="Nombre *" value={f.nombre} onChange={set('nombre')} required />
            <input className={IC} placeholder="Apellido" value={f.apellido} onChange={set('apellido')} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <select className={IC} value={f.tipo_persona} onChange={set('tipo_persona')}>
              <option value="fisica" className="bg-[#141414]">Persona física</option>
              <option value="juridica" className="bg-[#141414]">Persona jurídica</option>
            </select>
            <input className={IC} placeholder="DNI / CUIT" value={f.dni_cuit} onChange={set('dni_cuit')} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input className={IC} placeholder="Teléfono" value={f.telefono} onChange={set('telefono')} />
            <input className={IC} type="email" placeholder="Email" value={f.email} onChange={set('email')} />
          </div>
          <input className={IC} placeholder="Domicilio real" value={f.domicilio_real} onChange={set('domicilio_real')} />
          <input className={IC} placeholder="Domicilio legal" value={f.domicilio_legal} onChange={set('domicilio_legal')} />

          <div className="border-t border-white/10 pt-3">
            <p className="text-[11px] text-[#888] uppercase tracking-wide mb-2">Crear expediente al mismo tiempo (opcional)</p>
            <input className={IC + ' mb-2'} placeholder="Carátula" value={f.caratula} onChange={set('caratula')} />
            <div className="grid grid-cols-2 gap-3">
              <select className={IC} value={f.fuero} onChange={set('fuero')}>
                <option value="" className="bg-[#141414]">Fuero</option>
                {FUEROS.map((fu) => <option key={fu} value={fu} className="bg-[#141414]">{fu}</option>)}
              </select>
              <input className={IC} placeholder="Juzgado" value={f.juzgado} onChange={set('juzgado')} />
            </div>
          </div>

          <button type="submit" disabled={saving}
            className="w-full text-[#070707] font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 mt-2"
            style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Crear cliente'}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ─── Modal editar cliente ─────────────────────────────────────── */
function ModalEditar({ cliente, onClose, onSaved }) {
  const [f, setF] = useState({
    nombre: cliente.nombre || '',
    apellido: cliente.apellido || '',
    tipo_persona: cliente.tipo_persona || 'fisica',
    dni_cuit: cliente.dni_cuit || '',
    telefono: cliente.telefono || '',
    email: cliente.email || '',
    domicilio_real: cliente.domicilio_real || '',
    domicilio_legal: cliente.domicilio_legal || '',
    estado: cliente.estado || 'activo',
    notas: cliente.notas || '',
  });
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (ev) => {
    ev.preventDefault(); setErr(''); setSaving(true);
    try {
      await api.put(`/api/clientes/${cliente.id}`, f);
      onSaved();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail, 'Error al actualizar'));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-lg my-4 bg-[#0E0E0E] border border-[rgba(201,164,76,0.2)] rounded-2xl p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>Editar cliente</h3>
          <button onClick={onClose} className="text-[#888] hover:text-[#F8F6F0]"><X className="w-5 h-5" /></button>
        </div>
        {err && <div className="bg-red-500/10 border border-red-500/25 rounded-lg p-2.5 mb-4 text-red-400 text-xs">{err}</div>}
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input className={IC} placeholder="Nombre *" value={f.nombre} onChange={set('nombre')} required />
            <input className={IC} placeholder="Apellido" value={f.apellido} onChange={set('apellido')} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <select className={IC} value={f.tipo_persona} onChange={set('tipo_persona')}>
              <option value="fisica" className="bg-[#141414]">Persona física</option>
              <option value="juridica" className="bg-[#141414]">Persona jurídica</option>
            </select>
            <select className={IC} value={f.estado} onChange={set('estado')}>
              <option value="activo" className="bg-[#141414]">Activo</option>
              <option value="inactivo" className="bg-[#141414]">Inactivo</option>
            </select>
          </div>
          <input className={IC} placeholder="DNI / CUIT" value={f.dni_cuit} onChange={set('dni_cuit')} />
          <div className="grid grid-cols-2 gap-3">
            <input className={IC} placeholder="Teléfono" value={f.telefono} onChange={set('telefono')} />
            <input className={IC} type="email" placeholder="Email" value={f.email} onChange={set('email')} />
          </div>
          <input className={IC} placeholder="Domicilio real" value={f.domicilio_real} onChange={set('domicilio_real')} />
          <input className={IC} placeholder="Domicilio legal" value={f.domicilio_legal} onChange={set('domicilio_legal')} />
          <textarea className={IC + ' resize-none'} rows={2} placeholder="Notas internas" value={f.notas} onChange={set('notas')} />
          <button type="submit" disabled={saving}
            className="w-full text-[#070707] font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar cambios'}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ─── Panel detalle cliente ─────────────────────────────────────── */
function PanelDetalle({ clienteId, onClose }) {
  const [detalle, setDetalle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/api/clientes/${clienteId}`)
      .then((r) => setDetalle(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [clienteId]);

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={(e) => e.target === e.currentTarget && onClose()}>
    <div className="w-full max-w-lg bg-[#0A0A0A] border border-[rgba(201,164,76,0.2)] rounded-2xl shadow-2xl flex flex-col" style={{ maxHeight: '90vh' }}>
      <div className="flex items-center justify-between p-5 border-b border-[rgba(201,164,76,0.12)]">
        <h3 className="text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>
          {detalle?.cliente ? `${detalle.cliente.nombre} ${detalle.cliente.apellido || ''}`.trim() : 'Detalle'}
        </h3>
        <button onClick={onClose} className="text-[#888] hover:text-[#F8F6F0]"><X className="w-5 h-5" /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-5 min-h-0">
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#C9A44C]" /></div>
        ) : detalle ? (
          <div className="space-y-5">
            {/* Datos básicos */}
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9A44C] mb-2">Datos personales</p>
              <div className="space-y-1.5 text-sm">
                {detalle.cliente.dni_cuit && (
                  <div className="flex gap-2"><span className="text-[#888] w-24 flex-shrink-0">DNI/CUIT</span><span>{detalle.cliente.dni_cuit}</span></div>
                )}
                {detalle.cliente.telefono && (
                  <div className="flex gap-2"><span className="text-[#888] w-24 flex-shrink-0">Teléfono</span><span>{detalle.cliente.telefono}</span></div>
                )}
                {detalle.cliente.email && (
                  <div className="flex gap-2"><span className="text-[#888] w-24 flex-shrink-0">Email</span><span className="truncate">{detalle.cliente.email}</span></div>
                )}
                {detalle.cliente.domicilio_real && (
                  <div className="flex gap-2"><span className="text-[#888] w-24 flex-shrink-0">Domicilio</span><span>{detalle.cliente.domicilio_real}</span></div>
                )}
                {detalle.cliente.notas && (
                  <div className="flex gap-2"><span className="text-[#888] w-24 flex-shrink-0">Notas</span><span className="text-[#ccc]">{detalle.cliente.notas}</span></div>
                )}
              </div>
            </div>

            {/* Expedientes */}
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9A44C] mb-2">
                Expedientes ({detalle.expedientes?.length || 0})
              </p>
              {(!detalle.expedientes || detalle.expedientes.length === 0) ? (
                <p className="text-xs text-[#555]">Sin expedientes</p>
              ) : (
                <div className="space-y-2">
                  {detalle.expedientes.map((e) => (
                    <div key={e.id} className="bg-white/[0.02] border border-white/5 rounded-lg p-3">
                      <div className="text-xs font-medium truncate">{e.caratula}</div>
                      <div className="text-[10px] text-[#888] mt-0.5">
                        {e.fuero && `${e.fuero} · `}{e.estado_procesal?.replace('_', ' ')}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Honorarios */}
            {detalle.honorarios?.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9A44C] mb-2">
                  Honorarios ({detalle.honorarios.length})
                </p>
                <div className="space-y-2">
                  {detalle.honorarios.map((h) => (
                    <div key={h.id} className="bg-white/[0.02] border border-white/5 rounded-lg p-3 flex justify-between">
                      <div>
                        <div className="text-xs">{h.tipo}</div>
                        <div className="text-[10px] text-[#888]">{h.estado}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-[#C9A44C]">
                          ${Number(h.monto || 0).toLocaleString('es-AR')}
                        </div>
                        <div className="text-[10px] text-[#888]">
                          Pagado: ${Number(h.monto_pagado || 0).toLocaleString('es-AR')}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Co-partes */}
            {detalle.copartes?.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9A44C] mb-2">
                  Co-partes ({detalle.copartes.length})
                </p>
                <div className="space-y-1.5">
                  {detalle.copartes.map((cp) => (
                    <div key={cp.id} className="text-xs p-2 bg-white/[0.02] rounded-lg">
                      {cp.nombre} {cp.apellido} · {cp.dni_cuit}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-[#555]">Error al cargar datos</p>
        )}
      </div>
    </div>
    </div>
  );
}

/* ─── Página principal Clientes ─────────────────────────────────── */
export default function Clientes() {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [showCrear, setShowCrear] = useState(false);
  const [editando, setEditando] = useState(null);
  const [viendoId, setViendoId] = useState(null);
  const [expandido, setExpandido] = useState(null);

  const cargar = async () => {
    setLoading(true);
    try { const r = await api.get('/api/clientes/'); setClientes(r.data); }
    finally { setLoading(false); }
  };
  useEffect(() => { cargar(); }, []);

  const eliminar = async (id, nombre) => {
    if (!(await showConfirm(`¿Eliminar a ${nombre}? Esta acción es irreversible.`, { titulo: 'Eliminar cliente' }))) return;
    try {
      await api.delete(`/api/clientes/${id}`);
      cargar();
    } catch (e) {
      showAlert(e.response?.data?.detail || 'Error al eliminar');
    }
  };

  const filtrados = clientes.filter((c) => {
    const t = `${c.nombre || ''} ${c.apellido || ''} ${c.dni_cuit || ''} ${c.email || ''}`.toLowerCase();
    const ok_q = t.includes(q.toLowerCase());
    const ok_e = filtroEstado === 'todos' || c.estado === filtroEstado;
    return ok_q && ok_e;
  });

  return (
    <DashboardLayout title="Clientes">
      {/* Header con búsqueda y filtros */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#888]" />
          <input className={IC + ' pl-10'} placeholder="Buscar por nombre, apellido o documento..."
            value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <select className={IC + ' sm:w-40'} value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
          <option value="todos" className="bg-[#141414]">Todos</option>
          <option value="activo" className="bg-[#141414]">Activos</option>
          <option value="inactivo" className="bg-[#141414]">Inactivos</option>
        </select>
        <button onClick={() => setShowCrear(true)}
          className="text-[#070707] font-semibold px-5 py-2.5 rounded-lg flex items-center justify-center gap-2 whitespace-nowrap"
          style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}>
          <Plus className="w-4 h-4" /> Nuevo cliente
        </button>
      </div>

      {/* Contador */}
      <p className="text-xs text-[#666] mb-3">
        {filtrados.length} cliente{filtrados.length !== 1 ? 's' : ''}
        {q && ` · búsqueda: "${q}"`}
      </p>

      {/* Tabla */}
      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#C9A44C]" /></div>
      ) : filtrados.length === 0 ? (
        <div className="text-center py-20 text-[#666]">
          <Users className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">{clientes.length === 0 ? 'Aún no hay clientes registrados' : 'Sin resultados'}</p>
        </div>
      ) : (
        <div className="bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl overflow-hidden">
          {/* Header tabla */}
          <div className="hidden sm:grid grid-cols-[2fr_1fr_1.5fr_1fr_auto] gap-4 px-4 py-2.5 border-b border-white/5 text-[10px] uppercase tracking-[0.15em] text-[#666]">
            <span>Nombre</span>
            <span>Documento</span>
            <span>Contacto</span>
            <span>Estado</span>
            <span>Acciones</span>
          </div>
          {filtrados.map((c, idx) => (
            <div key={c.id} className={`border-b border-white/5 last:border-0 ${expandido === c.id ? 'bg-white/[0.02]' : ''}`}>
              <div className="grid grid-cols-[2fr_1fr_1.5fr_1fr_auto] gap-4 px-4 py-3 items-center hover:bg-white/[0.02] transition-all">
                {/* Nombre */}
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-[#C9A44C]/12 border border-[#C9A44C]/25 flex items-center justify-center text-[#C9A44C] text-xs flex-shrink-0">
                    {c.tipo_persona === 'juridica'
                      ? <Building2 className="w-3.5 h-3.5" />
                      : (c.apellido || c.nombre || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">
                      {c.apellido ? `${c.apellido.toUpperCase()}, ${c.nombre}` : c.nombre}
                    </div>
                    <div className="text-[10px] text-[#888]">{c.tipo_persona === 'juridica' ? 'Jurídica' : 'Física'}</div>
                  </div>
                </div>
                {/* Documento */}
                <div className="text-xs text-[#aaa] truncate hidden sm:block">{c.dni_cuit || '—'}</div>
                {/* Contacto */}
                <div className="hidden sm:block">
                  {c.telefono && <div className="flex items-center gap-1 text-[11px] text-[#999]"><Phone className="w-3 h-3" />{c.telefono}</div>}
                  {c.email && <div className="flex items-center gap-1 text-[11px] text-[#999] truncate"><Mail className="w-3 h-3" />{c.email}</div>}
                </div>
                {/* Estado */}
                <div className="hidden sm:block">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border ${
                    c.estado === 'activo'
                      ? 'text-green-300 bg-green-500/10 border-green-500/20'
                      : 'text-[#888] bg-white/5 border-white/10'
                  }`}>
                    {c.estado}
                  </span>
                </div>
                {/* Acciones */}
                <div className="flex items-center gap-1">
                  <button onClick={() => setViendoId(c.id)} title="Ver detalle"
                    className="p-1.5 text-[#888] hover:text-[#C9A44C] transition-colors">
                    <FileText className="w-4 h-4" />
                  </button>
                  <button onClick={() => setEditando(c)} title="Editar"
                    className="p-1.5 text-[#888] hover:text-[#C9A44C] transition-colors">
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button onClick={() => eliminar(c.id, `${c.nombre} ${c.apellido || ''}`)} title="Eliminar"
                    className="p-1.5 text-[#888] hover:text-red-400 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => setExpandido(expandido === c.id ? null : c.id)}
                    className="p-1.5 text-[#888] hover:text-[#F8F6F0] transition-colors">
                    {expandido === c.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              {/* Fila expandida (mobile info) */}
              {expandido === c.id && (
                <div className="px-4 pb-3 grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-[#888]">Documento: </span>{c.dni_cuit || '—'}</div>
                  <div><span className="text-[#888]">Estado: </span>{c.estado}</div>
                  {c.telefono && <div><span className="text-[#888]">Tel: </span>{c.telefono}</div>}
                  {c.email && <div className="col-span-2"><span className="text-[#888]">Email: </span>{c.email}</div>}
                  {c.domicilio_real && <div className="col-span-2"><span className="text-[#888]">Domicilio: </span>{c.domicilio_real}</div>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal detalle centrado */}
      {viendoId && (
        <PanelDetalle clienteId={viendoId} onClose={() => setViendoId(null)} />
      )}

      {showCrear && <ModalCrear onClose={() => setShowCrear(false)} onCreated={() => { setShowCrear(false); cargar(); }} />}
      {editando && <ModalEditar cliente={editando} onClose={() => setEditando(null)} onSaved={() => { setEditando(null); cargar(); }} />}
    </DashboardLayout>
  );
}
